import { useEffect, useMemo, useState } from "react";
import { BiBell, BiEnvelope, BiGridAlt, BiTime } from "react-icons/bi";
import { useToast } from "../../../components/Toast/ToastContext";
import {
  AccountSettings,
  apiErrorMessage,
  getNotificationPrefs,
  updateAccountSettings,
  updateNotificationPrefs,
} from "../../../services/settingsApi";
import {
  DASHBOARD_OPTIONS,
  DashboardChoice,
  getDefaultDashboard,
  setDefaultDashboard,
} from "../defaultDashboard";
import { Card, ChoiceChips, Field, Toggle, inputClass } from "../settingsUi";

const REMINDER_OPTIONS = [
  { value: "0", label: "No reminders" },
  { value: "60", label: "1 hour before" },
  { value: `${24 * 60}`, label: "1 day before" },
  { value: `${3 * 24 * 60}`, label: "3 days before" },
];

const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

const timezoneOptions = (): string[] => {
  try {
    const list = (Intl as any).supportedValuesOf?.("timeZone");
    if (Array.isArray(list) && list.length) return list;
  } catch {
    // older browsers: fall back to a short list
  }
  return [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Phoenix",
    "America/Los_Angeles",
    "America/Anchorage",
    "Pacific/Honolulu",
    "America/Toronto",
    "America/Vancouver",
    "Europe/London",
    "Europe/Paris",
    "Australia/Sydney",
    "Pacific/Auckland",
  ];
};

type Props = {
  settings: AccountSettings;
  onSaved: (next: AccountSettings) => void;
};

const PreferencesSection = ({ settings, onSaved }: Props) => {
  const { notify } = useToast();
  const [dashboard, setDashboard] = useState<DashboardChoice>(getDefaultDashboard);
  const [reminder, setReminder] = useState("0");
  const [timezone, setTimezone] = useState<string | null>(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [savingEmails, setSavingEmails] = useState(false);

  useEffect(() => {
    getNotificationPrefs()
      .then((prefs) => {
        setReminder(String(prefs.reminderOffsetMinutes));
        setTimezone(prefs.timezone);
      })
      .catch(() => notify("Couldn't load your reminder settings.", { type: "error" }))
      .finally(() => setPrefsLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const zones = useMemo(() => {
    const list = timezoneOptions();
    const detected = browserTimezone();
    return Array.from(new Set([timezone, detected, ...list].filter(Boolean) as string[]));
  }, [timezone]);

  const saveDashboard = (value: DashboardChoice) => {
    setDashboard(value);
    setDefaultDashboard(value);
    notify(`"Go to Dashboard" now opens ${value}.`, { type: "success" });
  };

  const savePrefs = async (next: { reminder?: string; timezone?: string | null }) => {
    const nextReminder = next.reminder ?? reminder;
    // Reminders need a time zone; use the browser's until one is picked.
    const nextTimezone =
      next.timezone !== undefined ? next.timezone : timezone ?? browserTimezone();
    const previous = { reminder, timezone };
    setReminder(nextReminder);
    setTimezone(nextTimezone);
    setSavingPrefs(true);
    try {
      await updateNotificationPrefs({
        reminderOffsetMinutes: Number(nextReminder) || 0,
        timezone: nextTimezone,
      });
      notify("Reminder settings saved.", { type: "success" });
    } catch (error) {
      setReminder(previous.reminder);
      setTimezone(previous.timezone);
      notify(apiErrorMessage(error, "Couldn't save reminder settings."), { type: "error" });
    } finally {
      setSavingPrefs(false);
    }
  };

  const saveMarketingEmails = async (enabled: boolean) => {
    setSavingEmails(true);
    try {
      onSaved(await updateAccountSettings({ marketingEmails: enabled }));
      notify(enabled ? "You'll get tips and product news." : "Tips and product emails turned off.", {
        type: "success",
      });
    } catch (error) {
      notify(apiErrorMessage(error, "Couldn't update email preferences."), { type: "error" });
    } finally {
      setSavingEmails(false);
    }
  };

  return (
    <div className="space-y-5">
      <Card
        icon={BiGridAlt}
        title="Default dashboard"
        description={'The page "Go to Dashboard" opens on this browser.'}
      >
        <ChoiceChips
          options={DASHBOARD_OPTIONS.map(({ value, label }) => ({ value, label }))}
          value={dashboard}
          onChange={saveDashboard}
        />
      </Card>

      <Card
        icon={BiBell}
        title="Event reminders"
        description="When we remind you about upcoming events, on every device you use."
      >
        <ChoiceChips
          options={REMINDER_OPTIONS}
          value={REMINDER_OPTIONS.some((o) => o.value === reminder) ? reminder : "0"}
          onChange={(value) => savePrefs({ reminder: value })}
          disabled={!prefsLoaded || savingPrefs}
        />
      </Card>

      <Card icon={BiTime} title="Time zone" description="Used for reminders and event times.">
        <Field
          label="Time zone"
          hint={
            browserTimezone() && timezone !== browserTimezone() ? (
              <button
                type="button"
                className="font-semibold text-blue-600 hover:underline"
                onClick={() => savePrefs({ timezone: browserTimezone() })}
                disabled={savingPrefs}
              >
                Use this computer's time zone ({browserTimezone()})
              </button>
            ) : undefined
          }
        >
          <select
            className={`${inputClass} sm:max-w-sm`}
            value={timezone ?? ""}
            onChange={(e) => savePrefs({ timezone: e.target.value || null })}
            disabled={!prefsLoaded || savingPrefs}
          >
            {!timezone && <option value="">Not set</option>}
            {zones.map((zone) => (
              <option key={zone} value={zone}>
                {zone.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </Field>
      </Card>

      <Card icon={BiEnvelope} title="Emails">
        <Toggle
          label="Tips and product news"
          description="Getting-started tips and what's new. Account emails like receipts and password resets always arrive."
          checked={settings.marketingEmails}
          onChange={saveMarketingEmails}
          disabled={savingEmails}
        />
      </Card>
    </div>
  );
};

export default PreferencesSection;
